/**
 * Dashboard data loading: both payloads in ONE state, plus the auto-refresh.
 *
 * Why both together: the "Kunden benachrichtigen" gate joins an `OverviewRow`
 * against the vulnerability-events payload (see `notifyGate`). Loading them in
 * two places would let the gate render before the events arrive and silently
 * degrade to "Bewertung ausstehend".
 *
 * Auto-refresh rules encoded here:
 *   - the interval is created in an effect and cleared on unmount and whenever
 *     it is paused, so nothing survives a route change,
 *   - the tick calls the LATEST loader through a ref (`loadRef`, re-assigned
 *     after every render): capturing `load` in the interval closure would keep
 *     refetching with the `only_affected` value that was current when the
 *     checkbox was ticked,
 *   - a tick is skipped while a request is in flight or the tab is hidden —
 *     every refresh walks the whole CMDB, so a queue of them is expensive,
 *   - `paused` (a dialog is open) suspends the interval entirely: refreshing
 *     behind an open advisory would swap the data the user is reading.
 *
 * A failed refresh keeps the previous rows on screen and only shows the error
 * banner: blanking a working table because one poll failed is worse than stale
 * data with a visible warning.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ApiError, errorMessage, fetchChangeStatus, fetchCveDashboardSoft, fetchDashboardOverview, fetchVulnerabilityEventsSoft,
} from '../../lib/api';
import type { DashboardData } from '../../lib/types';
import { AUTO_REFRESH_MS } from './dashboardModel';

type LoadSource = 'imperative' | 'automatic';

export interface UseDashboardDataOptions {
  /** The only filter that hits the server. */
  onlyAffected: boolean;
  /** True while a modal is open — the interval must not fire behind it. */
  paused: boolean;
  intervalMs?: number;
}

export interface DashboardDataState {
  /** null until the first successful load. */
  data: DashboardData | null;
  /** True while any request is in flight (initial load or refresh). */
  loading: boolean;
  /** German message of the last failed load, verbatim from the server. */
  error: string | null;
  syncStatus: 'pending' | 'retrying' | null;
  /** Imperative reload after a mutation; resolves when state has been updated. */
  refresh: () => Promise<void>;
}

export function useDashboardData({
  onlyAffected,
  paused,
  intervalMs = AUTO_REFRESH_MS,
}: UseDashboardDataOptions): DashboardDataState {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<'pending' | 'retrying' | null>(null);

  // Last request wins: a slow poll must not overwrite a newer reload.
  const seq = useRef(0);
  const inFlight = useRef(false);
  const alive = useRef(true);
  const pausedRef = useRef(paused);
  pausedRef.current = paused;

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async (source: LoadSource): Promise<void> => {
    const mySeq = ++seq.current;
    inFlight.current = true;
    setLoading(true);
    try {
      // Users without triage.read can still load the product overview;
      // service failures must remain visible instead of implying zero events.
      const [overview, events, cves] = await Promise.all([
        fetchDashboardOverview(onlyAffected),
        fetchVulnerabilityEventsSoft(),
        // DEV-133: the CVE view and the deadline tile; none without triage.read.
        fetchCveDashboardSoft(),
      ]);
      if (
        !alive.current ||
        mySeq !== seq.current ||
        (source === 'automatic' && pausedRef.current)
      ) return;
      setData({ overview, events, cves });
      setError(null);
    } catch (err) {
      if (
        !alive.current ||
        mySeq !== seq.current ||
        (source === 'automatic' && pausedRef.current)
      ) return;
      // A 401 is handled globally: AuthProvider drops the user and RequireAuth
      // redirects. An error banner would only flash behind that redirect.
      setError(err instanceof ApiError && err.status === 401 ? null : errorMessage(err));
    } finally {
      if (mySeq === seq.current) {
        inFlight.current = false;
        if (alive.current) setLoading(false);
      }
    }
  }, [onlyAffected]);

  // Initial load + reload whenever the server-side filter changes.
  useEffect(() => {
    void load('imperative');
  }, [load]);

  // Keep the interval's view of the loader fresh (no dependency array: this runs
  // after EVERY render, which is what makes the tick immune to stale closures).
  const loadRef = useRef(load);
  useEffect(() => {
    loadRef.current = load;
  });

  // Poll only the tiny durable notification counter. Expensive CMDB reads
  // happen on changes; the existing slower refresh remains a recovery path
  // for external writes and scheduler runs that bypass application events.
  const revision = useRef<number | null>(null);
  useEffect(() => {
    let disposed = false;
    let checking = false;
    const check = async (): Promise<void> => {
      if (checking || document.hidden || pausedRef.current) return;
      checking = true;
      try {
        const status = await fetchChangeStatus();
        if (disposed || pausedRef.current || document.hidden) return;
        setSyncStatus(status.pending ? (status.failed ? 'retrying' : 'pending') : null);
        if (inFlight.current) return;
        if (revision.current !== status.revision) {
          revision.current = status.revision;
          await loadRef.current('automatic');
        }
      } catch {
        // apiFetch handles expired sessions; keep the normal refresh fallback.
      } finally {
        checking = false;
      }
    };
    const timer = window.setInterval(() => { void check(); }, 3000);
    void check();
    return () => {
      disposed = true;
      window.clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (paused) return undefined;

    const refreshWhenVisible = (): void => {
      if (inFlight.current) return;
      if (document.hidden) return;
      void loadRef.current('automatic');
    };
    const onVisibilityChange = (): void => {
      if (!document.hidden) refreshWhenVisible();
    };

    const timer = window.setInterval(refreshWhenVisible, intervalMs);
    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', onVisibilityChange);
    // On mount this is skipped because the initial request is already in
    // flight; after closing a long-open dialog it refreshes immediately.
    refreshWhenVisible();

    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [paused, intervalMs]);

  const refresh = useCallback((): Promise<void> => load('imperative'), [load]);
  return { data, loading, error, syncStatus, refresh };
}
